export type Step = [number, number];

export type ChecklistStatus = "complete" | "current" | "upcoming";

export interface ChecklistItem {
  id: string;
  label: string;
  status: ChecklistStatus;
  aiActive?: boolean;
  meta?: string;
}

export interface GlobalProgressPayload {
  ownerStep?: Step | null;
  collaborators: Array<{ id: string; step?: Step | null }>;
  discoveryCompleted?: boolean;
}

function isValidStep(step: unknown): step is Step {
  return (
    Array.isArray(step) &&
    step.length >= 2 &&
    typeof step[0] === "number" &&
    typeof step[1] === "number"
  );
}

export function toStep(step: number[] | Step | null | undefined): Step | null {
  if (!step) return null;
  if (isValidStep(step)) return [step[0], step[1]];
  return null;
}

export function compareStep(a: Step | null | undefined, b: Step | null | undefined): number {
  const aa = a ?? null;
  const bb = b ?? null;
  if (!aa && !bb) return 0;
  if (!aa) return -1;
  if (!bb) return 1;
  if (aa[0] !== bb[0]) return aa[0] < bb[0] ? -1 : 1;
  if (aa[1] !== bb[1]) return aa[1] < bb[1] ? -1 : 1;
  return 0;
}

export function maxStep(a: Step | null | undefined, b: Step | null | undefined): Step | null {
  return compareStep(a, b) >= 0 ? (a ?? null) : (b ?? null);
}

export function isStepGte(step: Step | null | undefined, target: Step): boolean {
  return compareStep(step, target) >= 0;
}

export function isStepGt(step: Step | null | undefined, target: Step): boolean {
  return compareStep(step, target) > 0;
}

export function isStepInRange(step: Step | null | undefined, start: Step, end: Step): boolean {
  return compareStep(step, start) >= 0 && compareStep(step, end) <= 0;
}

export function getMaxStepFromAnswers(
  answers: Array<{ step?: number[] | Step | null | undefined }> | null | undefined
): Step | null {
  if (!answers || answers.length === 0) return null;
  let current: Step | null = null;
  for (const a of answers) {
    current = maxStep(current, toStep(a?.step));
  }
  return current;
}

/** GET /answer pour l’interview owner agrège aussi les subs : ne pas prendre le max sur ce mélange. */
export function getMaxStepFromAnswersForInterview(
  answers: Array<{
    step?: number[] | Step | null | undefined;
    interview_id?: string | null;
  }> | null | undefined,
  interviewId: string
): Step | null {
  if (!answers?.length) return null;
  const id = String(interviewId);
  const subset = answers.filter((a) => a?.interview_id != null && String(a.interview_id) === id);
  return getMaxStepFromAnswers(subset);
}

function hasStringContent(content: unknown): boolean {
  return content != null && String(content).trim().length > 0;
}

/**
 * Après delete-from-here, il peut ne rester qu’une (2,1) avec content vidé : le max step
 * reste (2,1) mais aucun vrai tour utilisateur — la checklist ne doit pas marquer
 * « interviews en cours » / 50 % comme si l’entretien collaborateur avait commencé.
 */
export function getEffectiveCollaboratorMaxStepFromAnswers(
  answers: Array<{
    step?: number[] | Step | null | undefined;
    content?: string | null;
  }> | null | undefined
): Step | null {
  if (!answers || answers.length === 0) return null;
  const ph2 = answers.filter((a) => toStep(a?.step)?.[0] === 2);
  if (ph2.length === 0) return null;
  const any20 = ph2.some((a) => toStep(a?.step)?.[0] === 2 && toStep(a?.step)?.[1] === 0);
  const any21 = ph2.some((a) => toStep(a?.step)?.[0] === 2 && toStep(a?.step)?.[1] === 1);
  const anySubAfter21 = ph2.some((a) => {
    const s = toStep(a?.step);
    return s != null && s[0] === 2 && s[1] > 1;
  });
  const any21WithUser = ph2.some(
    (a) => toStep(a?.step)?.[0] === 2 && toStep(a?.step)?.[1] === 1 && hasStringContent(a.content)
  );
  if (anySubAfter21 || any21WithUser) {
    return getMaxStepFromAnswers(answers);
  }
  // Cas de reset / delete-from-here: on peut avoir une ligne (2,1) vide.
  // On ne veut pas compter l'entretien "en cours" côté owner, mais on veut une checklist
  // collaborateur active (première étape courante) -> fallback sur (2,0).
  if (any21 || any20) return [2, 0];
  return null;
}

function collaboratorPhaseFraction(step: Step | null): number {
  // Aligné collaborator_GPT (interview IA) : (2,0) accueil → (2,1) séquence → (2,3) interdépendances
  // → (2,4) décisions → (2,7) irritants → (2,8)–(2,9) alignement / dernière question → (2,10) fin
  if (!step) return 0;
  if (compareStep(step, [2, 0]) >= 0 && compareStep(step, [2, 1]) < 0) return 0.05;
  if (compareStep(step, [2, 1]) < 0) return 0;
  if (compareStep(step, [2, 10]) >= 0) return 1;
  if (compareStep(step, [2, 8]) >= 0) return 0.8;
  if (compareStep(step, [2, 7]) >= 0) return 0.6;
  if (compareStep(step, [2, 4]) >= 0) return 0.4;
  if (compareStep(step, [2, 3]) >= 0) return 0.3;
  return 0.2; // (2,1) séquence chronologique
}

export function getGlobalProgress(payload: GlobalProgressPayload): number {
  const ownerStep = payload.ownerStep ?? null;
  const collaborators = payload.collaborators ?? [];
  const discoveryCompleted = payload.discoveryCompleted ?? false;

  // Phase weights:
  // - Owner setup (1.1 -> 1.5): 50%
  // - Interviews (2.1 -> 2.10, aggregated over collaborators): 50%
  const scopeStart: Step = [1, 1];
  const scopeEnd: Step = [1, 3];
  const inviteStart: Step = [1, 4];
  const inviteEnd: Step = [1, 5];

  let phase1 = 0;
  if (!ownerStep || compareStep(ownerStep, scopeStart) < 0) {
    phase1 = 0;
  } else if (isStepInRange(ownerStep, scopeStart, scopeEnd)) {
    const denom = scopeEnd[1] - scopeStart[1];
    const numer = ownerStep[1] - scopeStart[1];
    phase1 = denom > 0 ? (numer / denom) * 25 : 25;
  } else if (compareStep(ownerStep, inviteStart) < 0) {
    phase1 = 25;
  } else if (isStepInRange(ownerStep, inviteStart, inviteEnd)) {
    const denom = inviteEnd[1] - inviteStart[1];
    const numer = ownerStep[1] - inviteStart[1];
    phase1 = 25 + (denom > 0 ? (numer / denom) * 25 : 25);
  } else {
    phase1 = 50;
  }

  const totalInvited = collaborators.length;
  // [FIX SAMPLE PROCESS] Si pas de collaborateurs mais discovery terminé et owner >= [1,5], retourner 100%
  if (totalInvited === 0) {
    const inviteEnd: Step = [1, 5];
    if (discoveryCompleted && isStepGte(ownerStep, inviteEnd)) {
      return 100;
    }
    return Math.max(0, Math.min(100, Math.round(phase1)));
  }

  const sum = collaborators.reduce((acc, c) => acc + collaboratorPhaseFraction(c.step ?? null), 0);
  const phase2 = (sum / totalInvited) * 50;
  const total = phase1 + phase2;
  return Math.max(0, Math.min(100, Math.round(total)));
}

/** Pourcentage aligné sur les statuts de checklist (évite 0 % avec des étapes déjà cochées). */
export function progressPercentFromChecklist(items: ChecklistItem[]): number {
  if (!items.length) return 0;
  const w: Record<ChecklistStatus, number> = { complete: 1, current: 0.42, upcoming: 0 };
  const sum = items.reduce((acc, i) => acc + w[i.status], 0);
  return Math.max(0, Math.min(100, Math.round((sum / items.length) * 100)));
}

export function buildOwnerChecklistItems(input: {
  ownerStep: Step | null;
  collaborators: Array<{ id: string; step?: Step | null }>;
  /** True uniquement quand toutes les cartographies du processus sont prêtes (`is_finish`). */
  discoveryCompleted: boolean;
  t: (key: string) => string;
}): ChecklistItem[] {
  const ownerStep = input.ownerStep;
  const collaborators = input.collaborators ?? [];
  const discoveryCompleted = input.discoveryCompleted;
  const t = input.t;

  const scopeStart: Step = [1, 1];
  const scopeEnd: Step = [1, 3];
  const inviteStart: Step = [1, 4];
  const inviteEnd: Step = [1, 5];

  const anyInterviewStarted = collaborators.some((c) => isStepGte(c.step ?? null, [2, 1]));
  // [FIX SAMPLE PROCESS] Si aucun collaborateur mais discovery terminé (processus pré-chargé),
  // considérer les interviews comme terminées pour afficher la checklist complète
  const allInterviewsCompleted =
    (collaborators.length > 0 && collaborators.every((c) => isStepGte(c.step ?? null, [2, 10]))) ||
    (collaborators.length === 0 && discoveryCompleted && isStepGte(ownerStep, inviteEnd));

  const aiActiveScope = compareStep(ownerStep, [1, 3]) === 0;

  const setScopeStatus: ChecklistStatus = isStepGt(ownerStep, scopeEnd)
    ? "complete"
    : isStepInRange(ownerStep, scopeStart, scopeEnd)
      ? "current"
      : "upcoming";

  // Owner steps may never advance beyond 1.5, so treat the start of interviews (2.1 by any collaborator)
  // as the transition point that completes the invitation phase.
  const inviteStatus: ChecklistStatus = isStepGt(ownerStep, inviteEnd) || anyInterviewStarted
    ? "complete"
    : isStepInRange(ownerStep, inviteStart, inviteEnd)
      ? "current"
      : compareStep(ownerStep, inviteStart) < 0
        ? "upcoming"
        : "current";

  const interviewsInProgressStatus: ChecklistStatus = allInterviewsCompleted
    ? "complete"
    : anyInterviewStarted
      ? "current"
      : "upcoming";

  // Spec: "Completed" (interviews completed) only when ALL collaborators reach 2.10.
  const interviewsCompletedStatus: ChecklistStatus = allInterviewsCompleted ? "complete" : "upcoming";

  // Discovery completion is a post-condition (e.g. mapping finished), so it can become current
  // once interviews are completed but the overall discovery isn't marked as finished yet.
  const discoveryCompletedStatus: ChecklistStatus = !allInterviewsCompleted
    ? "upcoming"
    : discoveryCompleted
      ? "complete"
      : "current";

  const completedCount = collaborators.filter((c) => isStepGte(c.step ?? null, [2, 10])).length;
  const totalCount = collaborators.length;

  return [
    { id: "owner.setScope", label: t("checklist.setScope"), status: setScopeStatus, aiActive: aiActiveScope },
    { id: "owner.inviteStakeholders", label: t("checklist.inviteStakeholders"), status: inviteStatus },
    {
      id: "owner.interviewsInProgress",
      label: t("checklist.interviewsInProgress"),
      status: interviewsInProgressStatus,
      meta: totalCount > 0 ? `${completedCount}/${totalCount}` : undefined,
    },
    {
      id: "owner.interviewsCompleted",
      label: t("checklist.interviewsCompleted"),
      status: interviewsCompletedStatus,
      meta: totalCount > 0 ? `${completedCount}/${totalCount}` : undefined,
    },
    { id: "owner.discoveryCompleted", label: t("checklist.discoveryCompleted"), status: discoveryCompletedStatus },
  ];
}

/**
 * Checklist collaborateur — processus **IA** (`collaborator_GPT.second_step`).
 * Une ligne = une plage de `step` persistée sur les `Answer` ; le statut utilise le max(step) des réponses.
 *
 * | step (phase 2) | Branche backend |
 * | (2,0) | first_question — accueil |
 * | (2,1) | Séquence chronologique des tâches (boucle jusqu’à D4LIFE) |
 * | (2,3) | Interdépendances (pas de ligne dédiée en UI : regroupées sous « séquence » en attendant validation métier) |
 * | (2,4) | Points de décision / critères |
 * | (2,7) | Irritants (pain points) |
 * | (2,8)–(2,9) | Incohérences / alignement puis dernière question ((2,11) est une transition interne) |
 * | ≥ (2,10) | Fin d’entretien |
 */
export function buildCollaboratorChecklistItems(currentStep: Step | null, t: (key: string) => string): ChecklistItem[] {
  const mk = (id: string, labelKey: string, start: Step, end: Step): ChecklistItem => {
    const status: ChecklistStatus = isStepGt(currentStep, end)
      ? "complete"
      : isStepInRange(currentStep, start, end)
        ? "current"
        : "upcoming";
    return { id, label: t(labelKey), status };
  };

  return [
    // (2,1) + (2,3) interdépendances : une seule entrée checklist (ligne séparée retirée volontairement, front uniquement)
    mk("collab.mapTaskSequence", "checklist.mapTaskSequence", [2, 0], [2, 3]),
    mk("collab.identifyDecisionPoints", "checklist.identifyDecisionPoints", [2, 4], [2, 4]),
    mk("collab.highlightIrritants", "checklist.highlightIrritants", [2, 7], [2, 7]),
    mk("collab.reviewAlignment", "checklist.reviewAlignment", [2, 8], [2, 9]),
    {
      id: "collab.interviewCompleted",
      label: t("checklist.interviewCompleted"),
      status: isStepGte(currentStep, [2, 10]) ? "complete" : "upcoming",
    },
  ];
}

